import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import {
  Cancel as CancelIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import colorPalettes from '../../../themes/custom/colorPalettes';
import {
  InlineAlertProps,
  InlineAlertSize,
  InlineAlertVariant,
} from './InlineAlert.props';

// Size configurations based on Figma design
const sizeConfig: Record<
  InlineAlertSize,
  {
    padding: string;
    iconSize: number;
    typography: 'body1' | 'body2' | 'h6' | 'h5';
    buttonSize: 'small' | 'medium' | 'large';
    gap: number;
  }
> = {
  s: {
    padding: '6px 12px',
    iconSize: 16,
    typography: 'body2',
    buttonSize: 'small',
    gap: 6,
  },
  m: {
    padding: '8px 16px',
    iconSize: 20,
    typography: 'body2',
    buttonSize: 'small',
    gap: 8,
  },
  l: {
    padding: '8px 16px',
    iconSize: 24,
    typography: 'body1',
    buttonSize: 'medium',
    gap: 8,
  },
  xl: {
    padding: '12px 20px',
    iconSize: 28,
    typography: 'h6',
    buttonSize: 'large',
    gap: 12,
  },
};

// Theme configurations based on Figma design and color palette
const themeConfig: Record<
  InlineAlertVariant,
  {
    backgroundColor: string;
    borderColor: string;
    iconColor: string;
    textColor: string;
    icon: typeof CancelIcon;
  }
> = {
  error: {
    backgroundColor: colorPalettes.State['error-background'],
    borderColor: colorPalettes.State['error-pressed'],
    iconColor: colorPalettes.State['error-pressed'],
    textColor: colorPalettes.M3.black,
    icon: CancelIcon,
  },
  warning: {
    backgroundColor: colorPalettes.State['warning-background'],
    borderColor: colorPalettes.State['warning-pressed'],
    iconColor: colorPalettes.State['warning-pressed'],
    textColor: colorPalettes.M3.black,
    icon: WarningIcon,
  },
  success: {
    backgroundColor: colorPalettes.State['success-background'],
    borderColor: colorPalettes.State['success-pressed'],
    iconColor: colorPalettes.State['success-pressed'],
    textColor: colorPalettes.M3.black,
    icon: SuccessIcon,
  },
  info: {
    backgroundColor: colorPalettes.State['info-background'],
    borderColor: colorPalettes.State['info-pressed'],
    iconColor: colorPalettes.State['info-pressed'],
    textColor: colorPalettes.M3.black,
    icon: InfoIcon,
  },
};

export const InlineAlert: React.FC<InlineAlertProps> = ({
  variant = 'info',
  size = 'l',
  message,
  closable = false,
  onClose,
  closeIcon,
  sx,
  ...alertProps
}) => {
  const sizeStyles = sizeConfig[size];
  const themeStyles = themeConfig[variant];
  const IconComponent = themeStyles.icon;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: `${sizeStyles.gap}px`,
        padding: sizeStyles.padding,
        backgroundColor: themeStyles.backgroundColor,
        border: `2px solid ${themeStyles.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        ...sx,
      }}
      {...alertProps}
    >
      {/* Alert Icon */}
      <IconComponent
        sx={{
          color: themeStyles.iconColor,
          fontSize: sizeStyles.iconSize,
          flexShrink: 0,
          marginTop: 0,
        }}
      />

      {/* Content Section */}
      <Box sx={{ flex: '1 0 0', minWidth: 0 }}>
        <Typography
          variant={sizeStyles.typography}
          sx={{
            color: themeStyles.textColor,
            fontFamily: 'Archivo',
            fontWeight: 400,
            lineHeight: '24px',
            margin: 0,
          }}
        >
          {message}
        </Typography>
      </Box>

      {/* Close Button */}
      {closable && (
        <IconButton
          onClick={onClose}
          sx={{
            color: colorPalettes.M3.sys.light['on-surface-variant'],
            padding: '5.333px',
            borderRadius: '100px',
            flexShrink: 0,
            '&:hover': {
              backgroundColor:
                colorPalettes.M3['state-layers'].light['on-surface'][
                  'opacity-0.12'
                ],
            },
          }}
        >
          {closeIcon || <CloseIcon sx={{ fontSize: sizeStyles.iconSize }} />}
        </IconButton>
      )}
    </Box>
  );
};

export default InlineAlert;
